import { useState, useEffect } from 'react'
import { api } from '../../services/api'
import type { GlobalMetricsResponse, TenantSummary } from '../../services/api'

export default function JarixMetricsDashboard() {
  const [metrics, setMetrics] = useState<GlobalMetricsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchMetrics()
  }, [])

  const fetchMetrics = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await api.getGlobalMetrics()

      if (!result.success) {
        throw new Error(result.error || 'Error al cargar métricas')
      }

      setMetrics(result.data || null)
    } catch (err) {
      console.error('Error fetching metrics:', err)
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jarix-coral"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
        {error}
        <button onClick={fetchMetrics} className="ml-4 underline hover:no-underline">
          Reintentar
        </button>
      </div>
    )
  }

  if (!metrics) return null

  const { globalMetrics, alerts, ranking } = metrics
  const totalAlerts = alerts.criticalScore.length + alerts.highDetections.length +
                      alerts.missingCredentials.length + alerts.fetchErrors.length

  const mrr = globalMetrics.billing?.totalMonthlyRevenue || 0
  const arpu = globalMetrics.totalBillableUsers > 0
    ? Math.round(mrr / globalMetrics.totalBillableUsers)
    : 0
  const protectionRate = globalMetrics.totalDevices > 0
    ? Math.round((globalMetrics.totalProtectedDevices / globalMetrics.totalDevices) * 100)
    : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold text-white tracking-tight">Métricas Globales</h2>
          <p className="text-gray-400 text-sm font-sans">
            Vista consolidada de todos los clientes
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500 font-sans">
            Actualizado: {new Date(metrics.generatedAt).toLocaleString('es-CL')}
          </p>
          <button
            onClick={fetchMetrics}
            className="text-jarix-coral hover:text-jarix-coral-dark text-sm font-sans"
          >
            Actualizar
          </button>
        </div>
      </div>

      {/* Row 1: Business KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Clientes Activos"
          value={globalMetrics.activeTenants}
          subtitle={`${globalMetrics.totalTenants} totales`}
          icon="building"
          color="blue"
        />
        <StatCard
          title="Usuarios Gestionados"
          value={globalMetrics.totalBillableUsers}
          subtitle={`${globalMetrics.totalActiveUsers || 0} activos`}
          icon="users"
          color="coral"
        />
        <StatCard
          title="MRR Paquetes"
          value={`$${mrr.toLocaleString()}`}
          subtitle={`ARPU $${arpu}/usuario`}
          icon="dollar"
          color="green"
        />
        <StatCard
          title="Score Promedio"
          value={globalMetrics.averageSecurityScore}
          subtitle="de seguridad"
          icon="chart"
          color={globalMetrics.averageSecurityScore >= 70 ? 'green' : globalMetrics.averageSecurityScore >= 50 ? 'yellow' : 'red'}
        />
      </div>

      {/* Row 2: Operational Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Detecciones"
          value={globalMetrics.totalDetections}
          subtitle={`${globalMetrics.criticalDetections} críticas`}
          icon="alert"
          color={globalMetrics.criticalDetections > 0 ? 'red' : 'green'}
        />
        <StatCard
          title="Dispositivos Protegidos"
          value={globalMetrics.totalProtectedDevices}
          subtitle={`de ${globalMetrics.totalDevices} (${protectionRate}%)`}
          icon="shield"
          color="green"
        />
        <StatCard
          title="Usuarios Plataforma"
          value={globalMetrics.platformUsers}
          subtitle="registrados en Jarix"
          icon="monitor"
          color="blue"
        />
        <StatCard
          title="Clientes Suspendidos"
          value={globalMetrics.suspendedTenants}
          subtitle="requieren atención"
          icon="alert"
          color={globalMetrics.suspendedTenants > 0 ? 'yellow' : 'green'}
        />
      </div>

      {/* Alerts Panel */}
      {totalAlerts > 0 && (
        <div className="bg-red-900/20 border border-red-800 rounded-xl p-4">
          <h3 className="text-lg font-semibold text-red-400 mb-3 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Atención Requerida ({totalAlerts})
          </h3>
          <div className="space-y-2">
            {alerts.criticalScore.length > 0 && (
              <AlertItem
                type="critical"
                message={`${alerts.criticalScore.length} cliente(s) con Security Score crítico (<60)`}
                tenants={alerts.criticalScore}
              />
            )}
            {alerts.highDetections.length > 0 && (
              <AlertItem
                type="warning"
                message={`${alerts.highDetections.length} cliente(s) con más de 5 detecciones críticas`}
                tenants={alerts.highDetections}
              />
            )}
            {alerts.missingCredentials.length > 0 && (
              <AlertItem
                type="info"
                message={`${alerts.missingCredentials.length} cliente(s) activos sin credenciales configuradas`}
                tenants={alerts.missingCredentials}
              />
            )}
            {alerts.fetchErrors.length > 0 && (
              <AlertItem
                type="error"
                message={`${alerts.fetchErrors.length} cliente(s) con errores de conexión`}
                tenants={alerts.fetchErrors}
              />
            )}
          </div>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Client Ranking */}
        <div className="bg-gray-800 rounded-card-sm p-4 border border-gray-700/50">
          <h3 className="font-heading text-lg font-semibold text-white mb-4">Ranking de Clientes</h3>
          <div className="space-y-2">
            {ranking.length === 0 ? (
              <p className="text-gray-400 text-sm">No hay datos disponibles</p>
            ) : (
              ranking.slice(0, 10).map((tenant, index) => (
                <RankingRow key={tenant.id} tenant={tenant} position={index + 1} />
              ))
            )}
          </div>
        </div>

        {/* Distribution Charts */}
        <div className="space-y-4">
          {/* Plan Distribution with user counts */}
          <div className="bg-gray-800 rounded-card-sm p-4 border border-gray-700/50">
            <h3 className="font-heading text-lg font-semibold text-white mb-4">Distribución por Plan</h3>
            <div className="space-y-3">
              <PlanRow
                label="Advance"
                count={globalMetrics.planDistribution.advance.count}
                total={globalMetrics.totalTenants}
                price={globalMetrics.planDistribution.advance.pricePerUser}
                color="bg-purple-500"
              />
              <PlanRow
                label="Shield"
                count={globalMetrics.planDistribution.shield.count}
                total={globalMetrics.totalTenants}
                price={globalMetrics.planDistribution.shield.pricePerUser}
                color="bg-blue-500"
              />
              <PlanRow
                label="Essentials"
                count={globalMetrics.planDistribution.essentials.count}
                total={globalMetrics.totalTenants}
                price={globalMetrics.planDistribution.essentials.pricePerUser}
                color="bg-gray-500"
              />
            </div>
          </div>

          {/* User & Device Coverage */}
          <div className="bg-gray-800 rounded-card-sm p-4 border border-gray-700/50">
            <h3 className="font-heading text-lg font-semibold text-white mb-4">Cobertura</h3>
            <div className="space-y-4">
              {/* Users */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-400 text-sm">Usuarios facturables</span>
                  <span className="text-jarix-coral font-heading font-semibold text-lg">{globalMetrics.totalBillableUsers}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>{globalMetrics.totalActiveUsers || 0} activos</span>
                  <span>·</span>
                  <span>{(globalMetrics.totalBillableUsers - (globalMetrics.totalActiveUsers || 0))} suspendidos</span>
                  <span>·</span>
                  <span className="text-green-400">${mrr.toLocaleString()}/mes</span>
                </div>
              </div>
              {/* Devices */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-400 text-sm">Dispositivos protegidos</span>
                  <span className="text-green-400 font-heading font-semibold text-lg">{protectionRate}%</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${protectionRate}%` }} />
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                  <span>{globalMetrics.totalProtectedDevices} CrowdStrike</span>
                  <span>·</span>
                  <span>{globalMetrics.totalJumpCloudDevices || globalMetrics.totalDevices} JumpCloud</span>
                </div>
              </div>
            </div>
          </div>

          {/* Status Distribution */}
          <div className="bg-gray-800 rounded-card-sm p-4 border border-gray-700/50">
            <h3 className="font-heading text-lg font-semibold text-white mb-4">Estado de Clientes</h3>
            <div className="flex items-center justify-around">
              <StatusCircle label="Activos" value={globalMetrics.activeTenants} color="text-green-400" />
              <StatusCircle label="Trial" value={globalMetrics.trialTenants} color="text-yellow-400" />
              <StatusCircle label="Suspendidos" value={globalMetrics.suspendedTenants} color="text-red-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Subcomponents

function StatCard({ title, value, subtitle, icon, color }: {
  title: string
  value: number | string
  subtitle: string
  icon: 'users' | 'shield' | 'chart' | 'alert' | 'dollar' | 'building' | 'monitor' | 'file'
  color: 'green' | 'blue' | 'yellow' | 'red' | 'coral'
}) {
  const colorClasses = {
    green: 'text-green-400',
    blue: 'text-blue-400',
    yellow: 'text-yellow-400',
    red: 'text-red-400',
    coral: 'text-jarix-coral',
  }

  const icons = {
    users: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />,
    shield: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />,
    chart: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
    alert: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />,
    dollar: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
    building: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />,
    monitor: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />,
    file: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
  }

  return (
    <div className="bg-gray-800 rounded-card-sm p-4 border border-gray-700/50 hover:border-gray-600/50 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <p className="text-gray-400 text-sm font-sans">{title}</p>
        <svg className={`w-5 h-5 ${colorClasses[color]}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {icons[icon]}
        </svg>
      </div>
      <p className={`text-3xl font-heading font-semibold ${colorClasses[color]}`}>{value}</p>
      <p className="text-gray-500 text-xs mt-1 font-sans">{subtitle}</p>
    </div>
  )
}

function AlertItem({ type, message, tenants }: {
  type: 'critical' | 'warning' | 'info' | 'error'
  message: string
  tenants: TenantSummary[]
}) {
  const [expanded, setExpanded] = useState(false)

  const colors = {
    critical: 'bg-red-500/20 text-red-300',
    warning: 'bg-yellow-500/20 text-yellow-300',
    info: 'bg-blue-500/20 text-blue-300',
    error: 'bg-gray-500/20 text-gray-300',
  }

  return (
    <div className={`rounded-lg p-3 ${colors[type]}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm">{message}</p>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs underline hover:no-underline"
        >
          {expanded ? 'Ocultar' : 'Ver'}
        </button>
      </div>
      {expanded && (
        <div className="mt-2 text-xs space-y-1">
          {tenants.map(t => (
            <div key={t.id} className="flex items-center justify-between">
              <span>{t.name}</span>
              {t.securityData && <span>Score: {t.securityData.securityScore}</span>}
              {t.error && <span className="text-red-400">{t.error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function RankingRow({ tenant, position }: { tenant: TenantSummary; position: number }) {
  const score = tenant.securityData?.securityScore || 0
  const users = tenant.securityData?.totalUsers || 0
  const devices = tenant.securityData?.totalDevices || 0
  const scoreColor = score >= 80 ? 'text-green-400' : score >= 60 ? 'text-yellow-400' : 'text-red-400'
  const bgColor = score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-yellow-500' : 'bg-red-500'

  return (
    <div className="flex items-center gap-3 p-2 rounded-button hover:bg-gray-700/50 transition-colors">
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
        position <= 3 ? 'bg-jarix-coral text-white' : 'bg-gray-700 text-gray-400'
      }`}>
        {position}
      </span>
      <div
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: tenant.primaryColor }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{tenant.name}</p>
        <p className="text-gray-500 text-xs">
          {users} usuarios · {devices} dispositivos
        </p>
      </div>
      <div className="text-right">
        <p className={`text-lg font-bold ${scoreColor}`}>{score}</p>
        <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
          <div className={`h-full ${bgColor}`} style={{ width: `${score}%` }} />
        </div>
      </div>
    </div>
  )
}

function PlanRow({ label, count, total, price, color }: {
  label: string
  count: number
  total: number
  price: number
  color: string
}) {
  const percentage = total > 0 ? (count / total) * 100 : 0

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
          <span className="text-gray-300 text-sm">{label}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-500 text-xs">${price}/usr</span>
          <span className="text-white text-sm font-bold">{count}</span>
        </div>
      </div>
      <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden ml-[18px]">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  )
}

function StatusCircle({ label, value, color }: {
  label: string
  value: number
  color: string
}) {
  return (
    <div className="text-center">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-gray-400 text-xs">{label}</p>
    </div>
  )
}
